import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useTranslation } from "react-i18next";
import { getApiBaseURL } from "../../api/apiBaseUrl";
import { getSiteWebchatConfig, updateSiteWebchatConfig } from "../../api/siteWebchat";

const DEFAULTS = {
  accentColor: "#0F6FDE",
  position: "right",
  launcherLabel: "Fale conosco",
  launcherIcon: "none",
  launcherEmoji: "",
  openMode: "click",
  greetingTitle: "Olá",
  greetingMessage: "Como podemos ajudar?",
  prechatMode: "name",
  allowedDomains: "",
};

function workspaceCompany() {
  if (typeof window === "undefined") return "";
  return String(window.localStorage.getItem("company") || "").trim();
}

function installTargets(company, publicKey) {
  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://xpro4.xpro.me";
  const api = getApiBaseURL();
  return { origin, api, company, publicKey };
}

export function buildSiteWebchatSnippet({ company, publicKey }) {
  const { origin, api } = installTargets(company, publicKey);
  return `<script
  src="${origin}/webchat-widget.js"
  data-company="${company}"
  data-channel="${publicKey}"
  data-api="${api}"
  async>
</script>`;
}

export function buildReactInstall({ company, publicKey }) {
  const { origin, api } = installTargets(company, publicKey);
  return `import { useEffect } from "react";

export default function ChatNoSite() {
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "${origin}/webchat-widget.js";
    script.async = true;
    script.dataset.company = "${company}";
    script.dataset.channel = "${publicKey}";
    script.dataset.api = "${api}";
    document.body.appendChild(script);
    return () => {
      script.remove();
    };
  }, []);

  return null;
}`;
}

export function buildNextInstall({ company, publicKey }) {
  const { origin, api } = installTargets(company, publicKey);
  return `import Script from "next/script";

export default function RootLayout({ children }) {
  return (
    <html lang="pt-BR">
      <body>
        {children}
        <Script
          src="${origin}/webchat-widget.js"
          strategy="afterInteractive"
          data-company="${company}"
          data-channel="${publicKey}"
          data-api="${api}"
        />
      </body>
    </html>
  );
}`;
}

const EMOJI_CHOICES = ["💬", "👋", "🎧", "❓", "✉️"];

function escapePreviewAttr(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function buildPreviewSrcDoc(draft, { company, publicKey }) {
  const { origin, api } = installTargets(company || "preview", publicKey || "preview");
  const scriptTag = `<script src="${escapePreviewAttr(origin)}/webchat-widget.js" async
  data-preview="1"
  data-company="${escapePreviewAttr(company)}"
  data-channel="${escapePreviewAttr(publicKey)}"
  data-api="${escapePreviewAttr(api)}"
  data-accent="${escapePreviewAttr(draft.accentColor || DEFAULTS.accentColor)}"
  data-position="${draft.position === "left" ? "left" : "right"}"
  data-label="${escapePreviewAttr(draft.launcherLabel || DEFAULTS.launcherLabel)}"
  data-icon="${escapePreviewAttr(draft.launcherIcon || "none")}"
  data-emoji="${escapePreviewAttr(draft.launcherEmoji || "")}"
  data-open="${draft.openMode === "invite" ? "invite" : "click"}"
  data-greeting-title="${escapePreviewAttr(draft.greetingTitle || DEFAULTS.greetingTitle)}"
  data-greeting-message="${escapePreviewAttr(draft.greetingMessage || DEFAULTS.greetingMessage)}"
></script>`;
  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8" />
  <style>
    html, body { margin: 0; height: 100%; background: #f4f6f8; font-family: system-ui, sans-serif; }
    .bar { height: 28px; background: #e6eaee; display: flex; align-items: center; gap: 6px; padding: 0 10px; color: #667085; font-size: 12px; }
    .dot { width: 8px; height: 8px; border-radius: 50%; background: #d0d5dd; display: inline-block; }
    .page { padding: 16px; }
    .line { height: 10px; background: #e1e5ea; border-radius: 6px; margin-bottom: 10px; }
  </style>
</head>
<body>
  <div class="bar"><span class="dot"></span><span class="dot"></span><span class="dot"></span> seusite.com</div>
  <div class="page"><div class="line" style="width:70%"></div><div class="line" style="width:48%"></div></div>
  ${scriptTag}
</body>
</html>`;
}

function ChatPreview({ draft, company, publicKey }) {
  const [srcDoc, setSrcDoc] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setSrcDoc(buildPreviewSrcDoc(draft, { company, publicKey }));
    }, 280);
    return () => clearTimeout(timer);
  }, [draft, company, publicKey]);

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "#f4f6f8",
        height: 480,
      }}
    >
      <Box
        component="iframe"
        title="Prévia do chat no site"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        srcDoc={srcDoc}
        sx={{ width: "100%", height: "100%", border: 0, display: "block", bgcolor: "#f4f6f8" }}
      />
    </Box>
  );
}

const SiteWebchatChannelSection = forwardRef(function SiteWebchatChannelSection(
  { channelId, initialPublicKey },
  ref
) {
  const { t } = useTranslation("settings");
  const [draft, setDraft] = useState(DEFAULTS);
  const [publicKey, setPublicKey] = useState(initialPublicKey || "");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  const [installTab, setInstallTab] = useState(0);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    if (!channelId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    getSiteWebchatConfig(channelId)
      .then((config) => {
        if (cancelled || !config) return;
        setPublicKey(config.publicKey || "");
        setDraft({
          accentColor: config.accentColor || DEFAULTS.accentColor,
          position: config.position || DEFAULTS.position,
          launcherLabel: config.launcherLabel || DEFAULTS.launcherLabel,
          launcherIcon: config.launcherIcon || DEFAULTS.launcherIcon,
          launcherEmoji: config.launcherEmoji || "",
          openMode: config.openMode === "invite" ? "invite" : "click",
          greetingTitle: config.greetingTitle || DEFAULTS.greetingTitle,
          greetingMessage: config.greetingMessage || DEFAULTS.greetingMessage,
          prechatMode: config.prechatMode || DEFAULTS.prechatMode,
          allowedDomains: config.allowedDomains || "",
        });
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || "Não foi possível carregar o chat no site.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [channelId]);

  useImperativeHandle(ref, () => ({
    async save(nextChannelId) {
      const id = nextChannelId || channelId;
      if (!id) return null;
      setSaving(true);
      setError("");
      try {
        const saved = await updateSiteWebchatConfig(id, draft);
        if (saved?.publicKey) setPublicKey(saved.publicKey);
        return saved;
      } catch (err) {
        setError(err?.message || "Não foi possível salvar a aparência.");
        throw err;
      } finally {
        setSaving(false);
      }
    },
  }));

  const company = workspaceCompany();
  const snippet = useMemo(() => {
    if (!publicKey || !company) return "";
    return buildSiteWebchatSnippet({ company, publicKey });
  }, [company, publicKey]);
  const reactInstall = useMemo(() => {
    if (!publicKey || !company) return "";
    return buildReactInstall({ company, publicKey });
  }, [company, publicKey]);
  const nextInstall = useMemo(() => {
    if (!publicKey || !company) return "";
    return buildNextInstall({ company, publicKey });
  }, [company, publicKey]);

  const installGuides = useMemo(
    () => [
      {
        id: "html",
        label: "Site em HTML",
        code: snippet,
        codeLabel: "Código para colar no site",
        intro:
          "Use esta aba se você edita os arquivos do site, ou se alguém da equipe publica páginas em HTML. O botão só aparece nas páginas que receberem este código.",
        steps: [
          {
            title: "Abra o arquivo que fecha a página",
            body: "Na maioria dos sites ele se chama index.html. Se várias páginas usam o mesmo rodapé ou o mesmo layout, abra esse arquivo compartilhado: assim você cola uma vez e o botão entra em todas as páginas.",
          },
          {
            title: "Ache o final da página",
            body: "Role até perto do fim e encontre a linha </body>. Ela marca o fim do conteúdo visível. O código precisa ficar imediatamente acima dessa linha, não no meio do texto e não depois dela.",
          },
          {
            title: "Cole o código e publique",
            body: "Cole o bloco abaixo, salve o arquivo e envie para o servidor, do jeito que você já publica o site. Se cada página é um HTML separado, repita a colagem em cada arquivo. Páginas sem o código não mostram o botão.",
          },
          {
            title: "Confira no site publicado",
            body: "Abra o site numa janela anônima, ou recarregue com o cache limpo. O botão fica no canto que você escolheu na prévia. Clique nele: o chat precisa abrir. Se não aparecer, clique com o botão direito na página, escolha Exibir código-fonte e procure por webchat-widget.js. Se essa palavra não estiver no código publicado, o arquivo novo ainda não foi para o ar.",
          },
        ],
      },
      {
        id: "wordpress",
        label: "WordPress",
        code: snippet,
        codeLabel: "Código para colar no rodapé",
        intro:
          "No WordPress você não precisa editar o tema. Um plugin de cabeçalho e rodapé coloca o código em todas as páginas de uma vez. O botão aparece no site público, não dentro do painel do WordPress.",
        steps: [
          {
            title: "Instale um plugin para colar o código",
            body: "No painel, vá em Plugins → Adicionar novo e busque WPCode. Instale e ative. Ele também aparece com o nome Insert Headers and Footers. Qualquer plugin de cabeçalho e rodapé serve, se você já tiver um.",
          },
          {
            title: "Abra o rodapé, não o cabeçalho",
            body: "No WPCode, entre em Code Snippets → Header & Footer. Use a caixa Footer. O cabeçalho carrega cedo demais e pode atrasar a página. O rodapé coloca o botão em todas as páginas que usam o tema, depois que o conteúdo já apareceu.",
          },
          {
            title: "Cole, salve e limpe o cache",
            body: "Cole o código abaixo na caixa Footer e clique em Save. Se o site usa cache, como LiteSpeed, WP Rocket ou W3 Total Cache, esvazie o cache. Sem isso o visitante continua vendo a versão antiga, sem o botão.",
          },
          {
            title: "Outros jeitos, se você não quiser plugin",
            body: "Tema de blocos: Aparência → Editor → partes do modelo → Rodapé → bloco HTML personalizado → colar → Salvar. Tema clássico: Aparência → Editor de arquivos de tema → footer.php → colar logo antes de </body>. Atualização de tema pode apagar essa edição, por isso o plugin é mais seguro. No Elementor, use o construtor de rodapé e um widget HTML.",
          },
        ],
      },
      {
        id: "react",
        label: "React",
        code: reactInstall,
        codeLabel: "Componente para o projeto React",
        intro:
          "Em React o código não entra no HTML solto. Ele entra uma única vez no componente que envolve o aplicativo, para o botão existir em todas as telas sem duplicar.",
        steps: [
          {
            title: "Crie um componente só para o chat",
            body: "No projeto, crie um arquivo ChatNoSite.jsx, por exemplo em src/components. Cole nele o código abaixo. Ele carrega o script quando a tela abre e remove o script se o componente sair da página.",
          },
          {
            title: "Coloque esse componente uma vez só",
            body: "Importe ChatNoSite no arquivo que envolve o app: em geral App.jsx, App.tsx ou main.jsx, ao lado do restante do layout. Não coloque dentro de cada página. Se ele montar de novo a cada rota, podem aparecer dois botões.",
          },
          {
            title: "Suba o site e teste a visita",
            body: "Rode o projeto e abra o endereço público, não só a prévia do editor. O botão fica no canto da prévia deste canal. Navegue entre duas páginas: o botão precisa continuar único. Se o botão sumir ao trocar de tela, o componente não está no layout geral.",
          },
        ],
      },
      {
        id: "next",
        label: "Next.js",
        code: nextInstall,
        codeLabel: "Trecho para o layout do Next.js",
        intro:
          "No Next.js o lugar certo é o layout raiz, o arquivo que envolve todas as páginas. O componente Script do próprio Next carrega o chat depois que a página já pode ser usada, sem travar a abertura.",
        steps: [
          {
            title: "Abra o layout que envolve o site",
            body: "Se o projeto tem a pasta app, o arquivo é app/layout.js ou app/layout.tsx. Cole o código abaixo nesse layout, mantendo o {children} que já renderiza as páginas. O Script fica depois de {children}, ainda dentro de body.",
          },
          {
            title: "Se o projeto ainda usa a pasta pages",
            body: "Nesse caso não existe app/layout.js. Abra pages/_app.js e renderize o mesmo componente Script do Next uma vez, junto do componente da página. Não coloque o script em _document com perigo de carregar duas vezes. Uma vez no _app é suficiente.",
          },
          {
            title: "Publique e confira o botão",
            body: "Faça o deploy, ou rode o site em produção local, e abra uma página pública. O botão precisa aparecer no canto escolhido na prévia. strategy afterInteractive espera a página ficar interativa antes de carregar o chat, então o primeiro desenho da página não atrasa.",
          },
        ],
      },
      {
        id: "store",
        label: "Loja e sites prontos",
        code: snippet,
        codeLabel: "O mesmo código, no rodapé da loja",
        intro:
          "Shopify, Nuvemshop, Wix, Webflow e construtores parecidos têm uma área de código personalizado. O código é este mesmo. O que muda é o menu em que você cola, sempre no final da página.",
        steps: [
          {
            title: "Shopify",
            body: "Loja online → Temas → o tema publicado → Editar código. Abra layout/theme.liquid. Cole o código logo antes de </body>, salve e visualize a loja. Não cole dentro de uma seção que só existe numa página.",
          },
          {
            title: "Nuvemshop",
            body: "No administrador, procure o editor de layout ou os códigos adicionais do layout. Cole o código no rodapé, de forma que ele entre em todas as páginas da loja, e publique as alterações.",
          },
          {
            title: "Wix",
            body: "Configurações → Avançado → Código personalizado → Adicionar código. Cole o código, escolha Todas as páginas e a posição Body - fim. Aplique e publique o site. O editor do Wix não mostra o botão; o site publicado mostra.",
          },
          {
            title: "Webflow",
            body: "Project settings → Custom code → Footer code. Cole o código, salve e publique o site. Alteração só no designer, sem publicar, não aparece para o visitante.",
          },
        ],
      },
      {
        id: "gtm",
        label: "Google Tag Manager",
        code: snippet,
        codeLabel: "Código da tag HTML personalizada",
        intro:
          "Use o Tag Manager só se o site já é publicado por ele e você não edita o HTML. A tag precisa carregar em todas as páginas, senão o botão some em parte do site.",
        steps: [
          {
            title: "Crie uma tag de HTML personalizado",
            body: "No contêiner do site, Tags → Nova → HTML personalizado. Cole o código abaixo dentro da tag, sem alterar as palavras data-company e data-channel. Elas dizem a qual empresa e a qual canal este botão pertence.",
          },
          {
            title: "Dispare em todas as páginas",
            body: "No acionamento, escolha All Pages, ou o acionador que cobre todas as páginas do site. Se o acionador for só a home, o botão não aparece no restante. Salve a tag.",
          },
          {
            title: "Publique o contêiner",
            body: "Use Visualizar para abrir o site com o depurador do Tag Manager e confirmar que a tag disparou. Depois clique em Enviar e publique a versão. Enquanto a versão não for publicada, o visitante não vê o botão.",
          },
        ],
      },
    ],
    [snippet, reactInstall, nextInstall]
  );
  const activeGuide = installGuides[installTab] || installGuides[0];

  const copyText = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Não foi possível copiar. Selecione o código e copie manualmente.");
    }
  };

  const setField = (name) => (event) => {
    setDraft((prev) => ({ ...prev, [name]: event.target.value }));
  };

  return (
    <Grid item xs={12}>
      <Divider sx={{ my: 1.5 }} />
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
        {t("channelForm.siteWebchat.title", { defaultValue: "Chat no site" })}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
        {t("channelForm.siteWebchat.intro", {
          defaultValue:
            "Um botão no seu site. A pessoa escreve sem sair da página e a conversa aparece aqui, junto com WhatsApp e os outros canais.",
        })}
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t("channelForm.siteWebchat.appearance", { defaultValue: "Como vai aparecer" })}
          </Typography>
          {loading ? <CircularProgress size={22} /> : null}
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <TextField
                label={t("channelForm.siteWebchat.color", { defaultValue: "Cor do botão" })}
                type="color"
                value={draft.accentColor}
                onChange={setField("accentColor")}
                sx={{ width: 88 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                select
                fullWidth
                label={t("channelForm.siteWebchat.position", { defaultValue: "Lado da tela" })}
                value={draft.position}
                onChange={setField("position")}
              >
                <MenuItem value="right">
                  {t("channelForm.siteWebchat.positionRight", { defaultValue: "Direita" })}
                </MenuItem>
                <MenuItem value="left">
                  {t("channelForm.siteWebchat.positionLeft", { defaultValue: "Esquerda" })}
                </MenuItem>
              </TextField>
            </Stack>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                {t("channelForm.siteWebchat.openMode", { defaultValue: "Ao chegar no site" })}
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={draft.openMode === "invite" ? "invite" : "click"}
                onChange={(_, value) => {
                  if (!value) return;
                  setDraft((prev) => ({ ...prev, openMode: value }));
                }}
              >
                <ToggleButton value="click">
                  {t("channelForm.siteWebchat.openClick", { defaultValue: "Só o botão" })}
                </ToggleButton>
                <ToggleButton value="invite">
                  {t("channelForm.siteWebchat.openInvite", { defaultValue: "Janela aberta" })}
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                {draft.openMode === "invite"
                  ? t("channelForm.siteWebchat.openInviteHelp", {
                      defaultValue:
                        "A saudação já aparece acima do botão. A conversa só começa quando a pessoa clicar.",
                    })
                  : t("channelForm.siteWebchat.openClickHelp", {
                      defaultValue: "A janela abre somente quando a pessoa clicar no botão.",
                    })}
              </Typography>
            </Box>
            <TextField
              label={t("channelForm.siteWebchat.launcher", { defaultValue: "Texto do botão" })}
              value={draft.launcherLabel}
              onChange={setField("launcherLabel")}
              inputProps={{ maxLength: 40 }}
              helperText={t("channelForm.siteWebchat.launcherHelp", {
                defaultValue: "O que a pessoa lê antes de abrir o chat. O ícone fica antes desse texto.",
              })}
            />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                {t("channelForm.siteWebchat.icon", { defaultValue: "Ícone do botão" })}
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={draft.launcherIcon || "none"}
                onChange={(_, value) => {
                  if (!value) return;
                  setDraft((prev) => ({
                    ...prev,
                    launcherIcon: value,
                    launcherEmoji: value === "emoji" ? prev.launcherEmoji || "💬" : "",
                  }));
                }}
              >
                <ToggleButton value="none">
                  {t("channelForm.siteWebchat.iconNone", { defaultValue: "Só texto" })}
                </ToggleButton>
                <ToggleButton value="chat" aria-label="Balão de conversa">
                  <ChatBubbleOutlineIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="headset" aria-label="Atendente">
                  <SupportAgentIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="help" aria-label="Ajuda">
                  <HelpOutlineIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="emoji">Emoji</ToggleButton>
              </ToggleButtonGroup>
              {draft.launcherIcon === "emoji" ? (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.25 }}>
                  {EMOJI_CHOICES.map((emoji) => (
                    <Button
                      key={emoji}
                      size="small"
                      variant={draft.launcherEmoji === emoji ? "contained" : "outlined"}
                      onClick={() => setDraft((prev) => ({ ...prev, launcherEmoji: emoji }))}
                      sx={{ minWidth: 40, px: 1 }}
                    >
                      {emoji}
                    </Button>
                  ))}
                  <TextField
                    size="small"
                    label={t("channelForm.siteWebchat.emoji", { defaultValue: "Outro emoji" })}
                    value={draft.launcherEmoji || ""}
                    onChange={(event) =>
                      setDraft((prev) => ({ ...prev, launcherEmoji: event.target.value }))
                    }
                    inputProps={{ maxLength: 8 }}
                    sx={{ width: 140 }}
                  />
                </Stack>
              ) : null}
            </Box>
            <TextField
              label={t("channelForm.siteWebchat.greetingTitle", { defaultValue: "Título da saudação" })}
              value={draft.greetingTitle}
              onChange={setField("greetingTitle")}
              inputProps={{ maxLength: 80 }}
            />
            <TextField
              label={t("channelForm.siteWebchat.greeting", { defaultValue: "Mensagem de boas-vindas" })}
              value={draft.greetingMessage}
              onChange={setField("greetingMessage")}
              inputProps={{ maxLength: 500 }}
              multiline
              minRows={2}
            />
            <Button
              size="small"
              startIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setShowAdvanced((v) => !v)}
              sx={{ alignSelf: "flex-start" }}
            >
              {t("channelForm.siteWebchat.advanced", { defaultValue: "Avançado" })}
            </Button>
            <Collapse in={showAdvanced}>
              <Stack spacing={1.5}>
                <TextField
                  select
                  fullWidth
                  label={t("channelForm.siteWebchat.prechat", { defaultValue: "Antes de conversar" })}
                  value={draft.prechatMode}
                  onChange={setField("prechatMode")}
                  helperText={t("channelForm.siteWebchat.prechatHelp", {
                    defaultValue: "Pedir o nome deixa a solicitação identificada no chat.",
                  })}
                >
                  <MenuItem value="name">
                    {t("channelForm.siteWebchat.prechatName", { defaultValue: "Pedir o nome" })}
                  </MenuItem>
                  <MenuItem value="name_email">
                    {t("channelForm.siteWebchat.prechatEmail", {
                      defaultValue: "Pedir nome e e-mail",
                    })}
                  </MenuItem>
                  <MenuItem value="none">
                    {t("channelForm.siteWebchat.prechatNone", {
                      defaultValue: "Começar sem pedir dados",
                    })}
                  </MenuItem>
                </TextField>
                <TextField
                  label={t("channelForm.siteWebchat.domains", { defaultValue: "Sites permitidos" })}
                  value={draft.allowedDomains}
                  onChange={setField("allowedDomains")}
                  placeholder="loja.com.br"
                  helperText={t("channelForm.siteWebchat.domainsHelp", {
                    defaultValue:
                      "Opcional. Um site por linha. Em branco, o botão funciona em qualquer página em que o código estiver.",
                  })}
                  multiline
                  minRows={2}
                />
              </Stack>
            </Collapse>
            {saving ? (
              <Typography variant="caption" color="text.secondary">
                {t("channelForm.siteWebchat.saving", { defaultValue: "Salvando aparência…" })}
              </Typography>
            ) : null}
          </Stack>
        </Grid>
        <Grid item xs={12} md={5}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {t("channelForm.siteWebchat.preview", { defaultValue: "Prévia" })}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Esta prévia roda o script de verdade. Clique no botão para abrir o chat. Ao mudar cor, texto ou convite, a página recarrega com o código novo.
          </Typography>
          <ChatPreview draft={draft} company={company} publicKey={publicKey} />
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t("channelForm.siteWebchat.install", { defaultValue: "Instalar no site" })}
        </Typography>
        {!channelId || !publicKey ? (
          <Alert severity="info">
            {t("channelForm.siteWebchat.saveFirst", {
              defaultValue: "Salve o canal para gerar o código. A prévia ao lado já mostra como o botão vai ficar.",
            })}
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: 760 }}>
              Escolha onde o site é feito. O código de cada aba já traz os dados deste canal. O botão
              aparece em todo lugar que carregar esse código, no canto que você definiu na prévia.
            </Typography>
            <Tabs
              value={installTab}
              onChange={(_, value) => setInstallTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 1.5, borderBottom: 1, borderColor: "divider" }}
            >
              {installGuides.map((guide) => (
                <Tab key={guide.id} label={guide.label} />
              ))}
            </Tabs>
            <Typography variant="body2" sx={{ mb: 1.5, maxWidth: 760 }}>
              {activeGuide.intro}
            </Typography>
            <Stack spacing={1.5} sx={{ mb: 2, maxWidth: 760 }}>
              {activeGuide.steps.map((step, index) => (
                <Box key={step.title}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {index + 1}. {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    {step.body}
                  </Typography>
                </Box>
              ))}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
              {activeGuide.codeLabel}
            </Typography>
            <Box
              sx={{
                position: "relative",
                bgcolor: "#0f1720",
                color: "#e8eef6",
                borderRadius: 1.5,
                p: 1.5,
                pr: 12,
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: "0.78rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {activeGuide.code}
              <Button
                size="small"
                variant="contained"
                onClick={() => copyText(activeGuide.code)}
                startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                sx={{ position: "absolute", top: 8, right: 8 }}
              >
                {copied ? "Copiado" : "Copiar"}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Grid>
  );
});

export default SiteWebchatChannelSection;
